import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	DotsHorizontalOutline,
	forwardRef,
	HStack,
	IconButton,
	Image,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PencilOutline,
	Spinner,
	StyleProps,
	Text,
	TrashOutline,
	useDisclosure,
} from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';
import { useCurrentUser } from '@bonusly/who-am-i';

import { AudioAttachmentPlayer } from '../../audio/player';
import { CustomVideoPlayer } from '../../video/custom-video-player';
import { useActionInput } from '../context';
import {
	AttachmentCategory,
	AudioAttachment,
	EmployeeSpotlightAttachment,
	ImageAttachment,
	LinkPreviewAttachment,
	SocialAttachment,
	VideoAttachment,
} from '../lib/entities';
import { deleteFileFromBrowserStorage, getMimetypeFromFilename, isVideoFile } from '../utils';

// TODO - Allow user to abort upload
const renderLoading = () => (
	<Box position="relative" borderRadius="md" w="100%" style={{ paddingBottom: '56.25%' }} overflow="hidden">
		<Box position="absolute" top="0" left="0" width="100%" height="100%" bg="gray.900" display="flex" alignItems="center" justifyContent="center">
			<Spinner color="text.interactive" borderWidth="3px" size="lg" />
		</Box>
	</Box>
);

// Media attachment display (supports images, GIFs, and videos)
export const ActionMedia = forwardRef<StyleProps, 'div'>((props, ref) => {
	const { isLoadingAttachment: isLoading, cancelVideoGeneration, attachment, removeAttachment: clearAttachmentState, setAttachment } = useActionInput();
	const { ...boxProps } = props;
	const [canPlay] = useState(false);
	const videoRef = useRef<HTMLVideoElement>(null);
	const currentUser = useCurrentUser();
	const http = useHttpClient('v2');
	const { isOpen: isEditing, onOpen: openEdit, onClose: closeEdit } = useDisclosure();
	const [editedQuote, setEditedQuote] = useState('');

	// Explicitly load the video when it can play to fix a bug in mobile safari where the video would not play without user interaction
	useEffect(() => {
		if (canPlay && videoRef.current) {
			videoRef.current.load();
		}
	}, [canPlay]);

	const showMenu = isLoading || !!attachment;
	const isAudioAttachment = attachment?.category === AttachmentCategory.Audio;

	// Initialize edited quote when opening edit mode
	const handleEditQuote = useCallback(() => {
		if (isAudioAttachment) {
			setEditedQuote((attachment as AudioAttachment).quote || '');
			openEdit();
		}
	}, [attachment, isAudioAttachment, openEdit]);

	// Save edited quote — persist to server and update local state
	const handleSaveQuote = useCallback(() => {
		if (isAudioAttachment && attachment) {
			const audioAttachment = attachment as AudioAttachment;
			const audioId = audioAttachment.data.audio_id;

			// Persist to server
			if (audioId) {
				http.patch(`/audios/${audioId}`, { audio: { quote: editedQuote } }).catch(() => {
					console.error('Failed to persist quote update');
				});
			}

			// Update local state
			const updatedAttachment = new AudioAttachment({
				...audioAttachment.data,
				audio_quote: editedQuote,
			});
			setAttachment(updatedAttachment);
			closeEdit();
		}
	}, [attachment, editedQuote, isAudioAttachment, setAttachment, closeEdit, http]);

	const removeAttachment = () => {
		// Cancel the video generation if it is in progress but before the attachment is created
		cancelVideoGeneration && cancelVideoGeneration();
		deleteFileFromBrowserStorage();

		// Make the discard API call before clearing attachment state
		if (attachment) {
			switch (attachment.category) {
				case AttachmentCategory.Video:
					http.patch(`/videos/${(attachment as VideoAttachment).data.video_id}/discard`).catch(() => {
						console.error('Failed to discard video');
					});
					break;
				case AttachmentCategory.Audio:
					http.patch(`/audios/${(attachment as AudioAttachment).data.audio_id}/discard`).catch(() => {
						console.error('Failed to discard audio');
					});
					break;
				default:
					break;
			}
		}

		// Clear the attachment state after making the API call
		clearAttachmentState();
	};
	const { t } = useTranslation('action_input');

	const renderMedia = () => {
		if (!attachment) return null;
		switch (attachment.category) {
			case AttachmentCategory.Image: {
				const imageAttachment = attachment as ImageAttachment;
				return <Image borderRadius="md" objectFit="cover" src={imageAttachment.gif} h="100%" w="100%" alt={imageAttachment.altText} />;
			}
			case AttachmentCategory.Social: {
				const socialAttachment = attachment as SocialAttachment;
				return isVideoFile(socialAttachment.mp4) ? (
					<Box
						// Social videos are always muted looped and auto-played
						as="video" // <video element with chakraUI props.
						src={socialAttachment.mp4}
						borderRadius="md"
						objectFit="cover"
						h="100%"
						w="100%"
						muted
						autoPlay
						loop
						playsInline
						role="application"
						title={socialAttachment.altText}
					/>
				) : (
					<Image borderRadius="md" objectFit="cover" src={socialAttachment.mp4} h="100%" w="100%" alt={socialAttachment.altText} />
				);
			}
			case AttachmentCategory.Video: {
				const videoAttachment = attachment as VideoAttachment;
				const videoMimeType = getMimetypeFromFilename(videoAttachment.data.video_original_url);
				return (
					<Box position="relative" style={{ paddingBottom: '56.25%' }} borderRadius="md" overflow="hidden">
						<Box position="absolute" top="0" left="0" width="100%" height="100%">
							<CustomVideoPlayer
								videoId="video-attachment"
								title={videoAttachment.altText}
								poster={videoAttachment.data.video_poster_url}
								ref={videoRef}
								className="video-attachment"
								data-testid="video-attachment"
								defaultMuted={false}
								allowFullScreen={false}
							>
								{videoMimeType ? (
									<source data-target="original" src={videoAttachment.data.video_original_url} type={videoMimeType} />
								) : (
									<source data-target="original" src={videoAttachment.data.video_original_url} />
								)}
							</CustomVideoPlayer>
						</Box>
					</Box>
				);
			}
			case AttachmentCategory.EmployeeSpotlight: {
				const spotlightAttachment = attachment as EmployeeSpotlightAttachment;
				const aspectRatio =
					spotlightAttachment.width != null && spotlightAttachment.height != null ? `${spotlightAttachment.width} / ${spotlightAttachment.height}` : undefined;
				return (
					<Box
						bg="bg.surface"
						borderRadius="lg"
						border="1px solid"
						borderColor="border.default"
						overflow="hidden"
						p={3}
						pb={4}
						data-testid="employee-spotlight-attachment"
					>
						{spotlightAttachment.imageUrl && (
							<Image
								src={spotlightAttachment.imageUrl}
								width="100%"
								aspectRatio={aspectRatio}
								borderRadius="md"
								alt={spotlightAttachment.altText}
								data-testid="employee-spotlight-image"
							/>
						)}
						{spotlightAttachment.title && (
							<Text mt={4} fontWeight="semibold" fontSize="md" color="text.default" data-testid="employee-spotlight-title">
								{spotlightAttachment.title}
							</Text>
						)}
					</Box>
				);
			}
			case AttachmentCategory.LinkPreview: {
				const linkPreviewAttachment = attachment as LinkPreviewAttachment;
				return (
					<Box bg="bg.surface" borderRadius="lg" border="1px solid" borderColor="border.default" overflow="hidden" data-testid="link-preview-attachment">
						{linkPreviewAttachment.imageUrl && (
							<Box maxH="12.5rem" overflow="hidden" display="flex" justifyContent="center" bg="bg.subtle">
								<Image
									src={linkPreviewAttachment.imageUrl}
									maxW="100%"
									maxH="12.5rem"
									objectFit="contain"
									alt={linkPreviewAttachment.altText}
									data-testid="link-preview-image"
								/>
							</Box>
						)}
						<Box p={3}>
							{linkPreviewAttachment.title && (
								<Text fontWeight="semibold" fontSize="sm" color="text.default" noOfLines={2} data-testid="link-preview-title">
									{linkPreviewAttachment.title}
								</Text>
							)}
							{linkPreviewAttachment.description && (
								<Text mt={1} fontSize="xs" color="text.soft" noOfLines={2} data-testid="link-preview-description">
									{linkPreviewAttachment.description}
								</Text>
							)}
							{linkPreviewAttachment.siteName && (
								<Text mt={1} fontSize="xs" color="text.softer" data-testid="link-preview-site-name">
									{linkPreviewAttachment.siteName}
								</Text>
							)}
						</Box>
					</Box>
				);
			}
			case AttachmentCategory.Audio: {
				const audioAttachment = attachment as AudioAttachment;
				return (
					<AudioAttachmentPlayer
						audioUrl={audioAttachment.audioUrl || ''}
						quote={isEditing ? editedQuote : audioAttachment.quote}
						giverName={currentUser?.display_name}
						giverAvatarUrl={currentUser?.profile_pic_url || currentUser?.full_pic_url}
						duration={audioAttachment.duration}
						captionsUrl={audioAttachment.transcriptUrl}
						isEditing={isEditing}
						onQuoteChange={setEditedQuote}
						onSaveQuote={handleSaveQuote}
						onCancelEdit={closeEdit}
						data-testid="audio-attachment"
					/>
				);
			}
			default:
				return null;
		}
	};

	if (!isLoading && !attachment) {
		return null;
	}

	return (
		<Box pos="relative" {...boxProps} ref={ref}>
			{isLoading ? renderLoading() : renderMedia()}
			{showMenu && !isEditing && (
				<Menu>
					<MenuButton
						as={IconButton}
						icon={<DotsHorizontalOutline size="sm" />}
						aria-label={t('attachment_options', 'Attachment options')}
						position="absolute"
						right={2}
						top={2}
						size="sm"
						isRound
						variant="solid"
						bg="blackAlpha.600"
						color="white"
						_hover={{ bg: 'blackAlpha.700' }}
					/>
					<MenuList border="none" p={1} minW="150px">
						{isAudioAttachment && (
							<MenuItem onClick={handleEditQuote} borderRadius="md">
								<HStack spacing={2}>
									<PencilOutline size="sm" />
									<Text>{t('edit_quote', 'Edit Quote')}</Text>
								</HStack>
							</MenuItem>
						)}
						<MenuItem onClick={removeAttachment} borderRadius="md" color="error.fg">
							<HStack spacing={2}>
								<TrashOutline size="sm" />
								<Text>{t('remove_attachment', 'Remove')}</Text>
							</HStack>
						</MenuItem>
					</MenuList>
				</Menu>
			)}
		</Box>
	);
});

ActionMedia.displayName = 'ActionMedia';
